import { z } from "zod";

import { callEcwSoapEndpoint, EcwHttpMethod } from "@/src/lib/ehr/ecw/webecw/client";
import { ecwBool, ecwDate, ecwFlag, ecwInt } from "@/src/lib/ehr/ecw/webecw/coerce";
import { EcwHttpStatusError, EcwOperationFailedError } from "@/src/lib/ehr/ecw/webecw/errors";
import type { EcwSession } from "@/src/lib/ehr/ecw/webecw/session";
import { buildSoapEnvelopeSections, xmlArray } from "@/src/lib/ehr/ecw/webecw/xml";
import { NotFoundError } from "@/src/lib/error";
import { type RetryStrategy, withRetry } from "@/src/lib/retry";

export enum EcwEncounterType {
  Unknown = "0",
  OfficeVisit = "1",
  Telephone = "2",
  OutOfOffice = "3",
  Claim = "4",
  Lab = "5",
  Web = "6",
  EPrescription = "7",
  Dashboard = "8",
  HealowNotification = "10",
  CarePlanVirtual = "14",
}

/** Encounter type; values outside the known set decode as Unknown. */
const ecwEncounterType = z.nativeEnum(EcwEncounterType).catch(EcwEncounterType.Unknown);

const WcvAppointment = z
  .object({
    isWCVAppt: ecwBool,
    isAV: ecwBool,
  })
  .strict();

export type WcvAppointment = z.infer<typeof WcvAppointment>;

export const EcwEncounter = z
  .object({
    encounterID: z.string(),
    date: ecwDate,
    startTime: z.string(),
    visitType: z.string(),
    status: z.string(),
    doctorID: z.string(),
    reason: z.string(),
    encType: ecwEncounterType,
    name: z.string(),
    officeVisitType: ecwInt,
    encLock: ecwBool,
    FacName: z.string(),
    code: z.string(),
    EncId: z.string(),
    InBuffer: ecwBool,
    InBumpList: ecwBool,
    resourceId: z.string(),
    BookAndCaseNo: z.string(),
    ProgDisplay: ecwInt,
    HL7ID: z.string(),
    inPatientFlag: ecwBool,
    ccmrvisit: ecwBool,
    dermvisit: ecwBool,
    fertilityTypeVisit: ecwBool.optional(),
    addendumId: z.string(),
    occHealthType: ecwInt,
    visitCodeFlag: ecwInt,
    visitTypeDetails: z.string(),
    facilityId: z.string(),
    PregVisit: ecwBool,
    ufname: z.string(),
    ulname: z.string(),
    uminitial: z.string(),
    initials: z.string(),
    suffix: z.string(),
    ClaimReq: ecwBool,
    isGroupEncounter: ecwBool,
    wcvAppointment: z.preprocess((value) => (value === "" ? undefined : value), WcvAppointment.optional()),
  })
  .strict();
export type EcwEncounter = z.infer<typeof EcwEncounter>;

const EncountersData = z.preprocess(
  (value) => (value === "" || value == null ? { row: [] } : value),
  z.object({ row: xmlArray(EcwEncounter) }).strict()
);

const GetPtEncountersReturn = z
  .object({
    resultset: z.object({ data: EncountersData }).strict(),
    totalCount: ecwInt,
    status: z.object({ errorno: z.string(), description: z.string() }).strict(),
  })
  .strict();

const GET_PATIENT_ENCOUNTERS_PATH = "/mobiledoc/jsp/catalog/xml/getPtEncounters.jsp";

export const WEBECW_ENCOUNTER_400_RETRY_STRATEGY: RetryStrategy = {
  shouldRetry: (error) => error instanceof EcwHttpStatusError && error.status === 400,
  maxRetries: 2,
};

export interface EcwPatientEncounters {
  encounters: EcwEncounter[];
  totalCount: number | null;
}

export interface EcwPatientEncountersExchange {
  request: {
    body: Record<string, string>;
    method: "POST";
    path: string;
    query: Record<string, string>;
  };
  response: unknown;
}

async function loadPatientEncounters(
  session: EcwSession,
  patientId: string,
  onExchange?: (exchange: EcwPatientEncountersExchange) => void
): Promise<EcwPatientEncounters> {
  const request = {
    method: EcwHttpMethod.POST,
    path: GET_PATIENT_ENCOUNTERS_PATH,
    query: { PatientId: patientId, EncOptions: "0" },
    body: { WebPortalEnabled: "1" },
  } as const;
  const ret = await callEcwSoapEndpoint(session, request, GetPtEncountersReturn);

  onExchange?.({ request, response: ret });

  return {
    encounters: ret.resultset.data.row,
    totalCount: ret.totalCount,
  };
}

export async function fetchPatientEncountersWithMetadata(
  session: EcwSession,
  patientId: string,
  onExchange?: (exchange: EcwPatientEncountersExchange) => void
): Promise<EcwPatientEncounters> {
  const { data, error } = await withRetry(
    "fetchPatientEncounters",
    () =>
      loadPatientEncounters(session, patientId, onExchange).then((encounters) => ({ data: encounters, error: null })),
    { strategy: WEBECW_ENCOUNTER_400_RETRY_STRATEGY }
  );

  if (error) throw error;

  return data!;
}

/** Reads encounters by eCW's internal patient `pid`, not the visible MRN/AccountNo. */
export async function fetchPatientEncounters(session: EcwSession, patientId: string): Promise<EcwEncounter[]> {
  return (await fetchPatientEncountersWithMetadata(session, patientId)).encounters;
}

export interface CreateTelephoneEncounterParams {
  /** eCW's internal patient `pid`, not the visible MRN/AccountNo. */
  patientId: string;
  providerId: string;
  facilityId: string;
  facilityPos: string;
  assignedToId: string;
  assignedToName: string;
  reason: string;
  caller: string;
  message: string;
  isHighPriority: boolean;
  date: string;
  time: string;
}

const CreateTelephoneEncounterReturn = z
  .object({
    isPatientDeleted: ecwBool.optional(),
    status: z.string().transform((status) => status.trim().toLowerCase()),
    encounterID: z.string().optional(),
    ticketId: z.string().optional(),
  })
  .passthrough()
  .superRefine((response, ctx) => {
    if (response.status !== "success") return;

    if (typeof response.isPatientDeleted !== "boolean") {
      ctx.addIssue({ code: "custom", message: "required for a successful response", path: ["isPatientDeleted"] });
    }

    if (!response.encounterID?.trim()) {
      ctx.addIssue({ code: "custom", message: "required for a successful response", path: ["encounterID"] });
    }
  })
  .transform((response) =>
    response.status === "success"
      ? {
          kind: "success" as const,
          encounterID: response.encounterID!,
          isPatientDeleted: response.isPatientDeleted!,
          response,
        }
      : { kind: "rejected" as const, response }
  );

export function buildCreateTelephoneEncounterSoapXml(
  session: EcwSession,
  params: CreateTelephoneEncounterParams
): string {
  return buildSoapEnvelopeSections([
    {
      wrapper: "encounter",
      fields: {
        id: params.patientId,
        date: params.date,
        time: params.time,
        endTime: params.time,
        DateTimeEdit: "false",
        visitType: "TEL",
        reason: params.reason,
        doctorid: params.providerId,
        status: "",
        billing: "",
        encType: EcwEncounterType.Telephone,
        notes: "",
        visitSubType: "",
        facilityId: params.facilityId,
        POS: params.facilityPos,
        arrTime: "00:00:00",
        depTime: "00:00:00",
        ptFlag: "0",
        Dx: "",
        visitTypeOverriden: "",
        refPrName: "",
        refPrId: "",
        DeptId: "0",
        UserInfo: "",
        resourceId: "",
        resFacFilterId: "0",
        practiceId: "0",
        transitionofcare: "0",
        isTelEncWeb: "1",
        ptEmail: "",
        populatetelencdata: "true",
      },
    },
    {
      wrapper: "telencounter",
      fields: {
        encounterid: "0",
        uid: session.trUserId,
        caller: params.caller,
        message: params.message,
        actiontaken: "",
        notes: "",
        priority: ecwFlag(params.isHighPriority),
        AssignedToId: params.assignedToId,
        assignedTo: params.assignedToName,
        pmcid: "0",
        LoginID: session.trUserId,
        virtualflag: "0",
        UIScreenName: "Telephone Encounter",
        TopClass: "",
        isAddressed: "false",
      },
    },
  ]);
}

const CREATE_TELEPHONE_ENCOUNTER_PATH = "/mobiledoc/jsp/catalog/xml/newEncounter.jsp";

/** Mirrors eCW's New Telephone Encounter save action. */
export async function createTelephoneEncounter(
  session: EcwSession,
  params: CreateTelephoneEncounterParams,
  onResponse?: (response: Record<string, unknown>) => void
): Promise<string> {
  const result = await callEcwSoapEndpoint(
    session,
    {
      method: EcwHttpMethod.POST,
      path: CREATE_TELEPHONE_ENCOUNTER_PATH,
      body: { FormData: buildCreateTelephoneEncounterSoapXml(session, params) },
    },
    CreateTelephoneEncounterReturn
  );

  onResponse?.(result.response);

  if (result.kind === "rejected") {
    throw new EcwOperationFailedError(CREATE_TELEPHONE_ENCOUNTER_PATH, "newEncounter returned a non-success status");
  }

  if (result.encounterID === "0" || result.isPatientDeleted) {
    throw new EcwOperationFailedError(
      CREATE_TELEPHONE_ENCOUNTER_PATH,
      "newEncounter did not return an active encounter"
    );
  }

  return result.encounterID;
}

export const EcwEncounterDetail = z
  .object({
    encounterId: z.string(),
    patientId: z.string(),
    webenabled: ecwBool,
    patientStatus: ecwInt,
    name: z.string(),
    time: z.string(),
    startTime: z.string(),
    endTime: z.string(),
    times: z.string(),
    arrivedTime: z.string(),
    timeIn: z.string(),
    timeOut: z.string(),
    roomno: z.string(),
    stsAfterArr: z.string(),
    stsAfterChkIn: z.string(),
    depTime: z.string(),
    dateIn: ecwDate,
    status: z.string(),
    dateOut: ecwDate,
    date: ecwDate,
    visitType: z.string(),
    visitTypeDesc: z.string(),
    visitCodeFlag: ecwInt,
    encLock: ecwBool,
    facilityId: z.string(),
    addendum: z.string(),
    qmbflag: ecwBool,
    encType: ecwEncounterType,
    ProviderId: z.string(),
    reason: z.string(),
    sex: z.string(),
    dob: ecwDate,
    obGynFlag: ecwBool,
    pregnancyVisitFlag: ecwBool,
    orthoVisitFlag: ecwBool,
    ResourceId: z.string(),
    visionVisitFlag: ecwBool,
    occHealthVisitFlag: ecwBool,
    dentalVisitFlag: ecwBool,
    dermVisitFlag: ecwBool,
    CarePlanVisitFlag: ecwBool,
    NuclearTestVisitFlag: ecwBool,
    CCMRVisitFlag: ecwBool,
    PhysicalTherapyVisitFlag: ecwBool,
    woundCareVisitFlag: ecwBool.optional(),
    DefaultViewForPhysicalTherapyVisit: ecwBool,
    isTelemed: ecwBool,
    PhysicalTherapy: ecwBool,
    ResourceType: ecwInt,
    unsavedScribeNotes: ecwInt,
  })
  .strict();
export type EcwEncounterDetail = z.infer<typeof EcwEncounterDetail>;

export const EcwEncounterDetailPatient = z
  .object({
    accountNumber: z.string(),
    dateOfBirth: ecwDate,
    firstName: z.string(),
    id: z.string(),
    lastName: z.string(),
    middleInitial: z.string(),
    sex: z.string(),
    suffix: z.string(),
    deceasedFlag: ecwBool,
    inactiveStatus: ecwBool,
    preferredName: z.string(),
    defaultGrowthchart: ecwInt,
    gestationalAge: z.string(),
  })
  .strict();
export type EcwEncounterDetailPatient = z.infer<typeof EcwEncounterDetailPatient>;

export interface EncounterDetailResponse {
  encounter: EcwEncounterDetail;
  patient: EcwEncounterDetailPatient | null;
}

const GetValuesForEnctrIdReturn = z.union([
  z
    .object({
      encounter: xmlArray(EcwEncounterDetail),
      patient: xmlArray(EcwEncounterDetailPatient),
    })
    .strict(),
  z.string().refine((value) => value.trim().length === 0, "unexpected non-empty getValuesForEnctrId return"),
]);

export async function fetchEncounterDetail(session: EcwSession, encounterId: string): Promise<EncounterDetailResponse> {
  const ret = await callEcwSoapEndpoint(
    session,
    {
      method: EcwHttpMethod.GET,
      path: "/mobiledoc/jsp/catalog/xml/getValuesForEnctrId.jsp",
      query: { encounterId, addPtDetails: "true", noAccessSection: "true" },
    },
    GetValuesForEnctrIdReturn
  );

  if (typeof ret === "string") throw new NotFoundError("Encounter not found", { encounterId });

  const encounter = ret.encounter[0];

  if (!encounter) throw new NotFoundError("Encounter not found", { encounterId });

  return { encounter, patient: ret.patient[0] ?? null };
}
